/* TreasurerDashboardTab — board-ready financial roll-up by account code. */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  TrendingUp, TrendingDown, DollarSign, Receipt, FileSpreadsheet, Loader2,
  Calendar, RefreshCw,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;

const todayIso = () => new Date().toISOString().slice(0, 10);
const firstOfMonth = () => { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); };
const firstOfYear = () => `${new Date().getFullYear()}-01-01`;

const PRESETS = [
  { id: 'mtd', label: 'Month to date', from: firstOfMonth, to: todayIso },
  { id: 'ytd', label: 'Year to date', from: firstOfYear, to: todayIso },
  { id: 'all', label: 'All time', from: () => '', to: () => '' },
];

const TreasurerDashboardTab = () => {
  const [preset, setPreset] = useState('mtd');
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(todayIso());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const applyPreset = (id) => {
    setPreset(id);
    const p = PRESETS.find((x) => x.id === id);
    if (p) { setFrom(p.from()); setTo(p.to()); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (from) params.set('date_from', from);
      if (to) params.set('date_to', to);
      const r = await axios.get(`${BACKEND_URL}/api/admin/treasurer/report?${params}`,
        { withCredentials: true });
      setData(r.data);
    } catch (e) {
      setData({ error: e.response?.data?.detail || 'Could not load report.' });
    }
    setLoading(false);
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const downloadCsv = () => {
    const params = new URLSearchParams();
    if (from) params.set('date_from', from);
    if (to) params.set('date_to', to);
    window.open(`${BACKEND_URL}/api/admin/treasurer/report.csv?${params}`, '_blank');
  };

  const summary = data?.summary || {};

  return (
    <div className="space-y-4" data-testid="treasurer-dashboard-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-heading text-xl font-bold text-[#0A0A0A] inline-flex items-center gap-2">
            <Receipt className="w-5 h-5 text-[#BA1826]" /> Treasurer Report
          </h2>
          <p className="text-xs text-[#4B5563] mt-1 max-w-2xl">
            Board-ready roll-up of every money-touching transaction by account code.
            Donations are auto-tagged from payment_transactions, shop sales from shop_orders,
            and expenses from their account_code field.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={load} className="text-xs px-2.5 py-1.5 border border-[#E5E7EB] rounded inline-flex items-center gap-1.5"
            data-testid="treasurer-refresh"><RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh</button>
          <button onClick={downloadCsv} className="text-xs px-3 py-1.5 bg-[#0A0A0A] text-white rounded inline-flex items-center gap-1.5"
            data-testid="treasurer-csv-btn">
            <FileSpreadsheet className="w-3 h-3" /> Download CSV
          </button>
        </div>
      </div>

      {/* Date range controls */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-3 flex items-center gap-2 flex-wrap" data-testid="treasurer-range">
        <Calendar className="w-4 h-4 text-[#BA1826]" />
        {PRESETS.map((p) => (
          <button key={p.id} onClick={() => applyPreset(p.id)}
            className={`text-[11px] px-2.5 py-1 rounded-full border font-semibold ${preset === p.id ? 'bg-[#BA1826] text-white border-[#BA1826]' : 'bg-white border-[#E5E7EB] text-[#4B5563]'}`}
            data-testid={`treasurer-preset-${p.id}`}>
            {p.label}
          </button>
        ))}
        <input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPreset(''); }}
          className="text-xs px-2 py-1 border border-[#E5E7EB] rounded" data-testid="treasurer-from" />
        <span className="text-xs text-[#9CA3AF]">→</span>
        <input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPreset(''); }}
          className="text-xs px-2 py-1 border border-[#E5E7EB] rounded" data-testid="treasurer-to" />
      </div>

      {loading && !data ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#BA1826]" /></div>
      ) : data?.error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded">{data.error}</div>
      ) : (
        <>
          {/* Summary tiles — the numbers a board treasurer actually wants */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="treasurer-summary-tiles">
            <SummaryTile Icon={TrendingUp} label="Gross donations (41xx)" value={fmtMoney(summary.gross_donations)} accent="green" testid="t-gross-donations" />
            <SummaryTile Icon={DollarSign} label="Gross merch revenue (42xx)" value={fmtMoney(summary.gross_merch_revenue)} accent="green" testid="t-gross-merch" />
            <SummaryTile Icon={TrendingDown} label="Processing fees (6115)" value={fmtMoney(summary.processing_fees)} accent="amber" testid="t-processing-fees" />
            <SummaryTile Icon={DollarSign} label="Net funds available" value={fmtMoney(summary.net_funds_available)} accent="brand" testid="t-net-funds" />
            <SummaryTile Icon={TrendingUp} label="Grants & sponsorships (43xx)" value={fmtMoney(summary.grants_sponsorships)} accent="green" testid="t-grants" />
            <SummaryTile Icon={DollarSign} label="Total revenue" value={fmtMoney(summary.total_revenue)} accent="brand" testid="t-total-revenue" />
            <SummaryTile Icon={TrendingDown} label="Total expenses" value={fmtMoney(summary.total_expenses)} accent="amber" testid="t-total-expenses" />
            <SummaryTile Icon={DollarSign} label="Net income" value={fmtMoney(summary.net_income)}
              accent={(summary.net_income || 0) >= 0 ? 'green' : 'amber'} testid="t-net-income" />
          </div>

          {/* Rollup tables */}
          <div className="grid lg:grid-cols-2 gap-4">
            <RollupTable title="Revenue by code" rows={data?.revenue_rows || []} total={summary.total_revenue} testid="treasurer-revenue-table" emptyText="No revenue in this period." />
            <RollupTable title="Expenses by code" rows={data?.expense_rows || []} total={summary.total_expenses} testid="treasurer-expense-table" emptyText="No expenses in this period." />
          </div>

          {/* Functional Expense Breakdown — Form 990 / FASB ASC 958 */}
          <FunctionalExpensesCard fx={data?.functional_expenses} totalExpenses={summary.total_expenses} />
        </>
      )}
    </div>
  );
};

const FX_COLOR = {
  program: { bar: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200' },
  administrative: { bar: 'bg-slate-500', text: 'text-slate-700', bg: 'bg-slate-50', border: 'border-slate-200' },
  fundraising: { bar: 'bg-rose-500', text: 'text-rose-700', bg: 'bg-rose-50', border: 'border-rose-200' },
  unclassified: { bar: 'bg-amber-400', text: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200' },
};

const FX_LABEL = {
  program: 'Program',
  administrative: 'Administrative',
  fundraising: 'Fundraising',
  unclassified: 'Unclassified',
};

const FunctionalExpensesCard = ({ fx, totalExpenses }) => {
  if (!fx) return null;
  const amounts = fx.amounts || {};
  const pcts = fx.percentages || {};
  const keys = ['program', 'administrative', 'fundraising', 'unclassified'];
  const programPct = pcts.program || 0;
  const overheadPct = (pcts.administrative || 0) + (pcts.fundraising || 0);
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg p-4" data-testid="functional-expenses-card">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-[#4B5563]">Functional Expense Breakdown</p>
          <p className="text-[10px] text-[#9CA3AF] mt-0.5 max-w-md">
            How every expense dollar is allocated across Program, Administrative, and
            Fundraising — the breakdown IRS Form 990 and most grants ask for.
          </p>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-wider text-[#9CA3AF] font-bold">Program Ratio</p>
          <p className="font-heading text-2xl font-bold text-emerald-700" data-testid="program-ratio">
            {programPct.toFixed(1)}%
          </p>
          <p className="text-[10px] text-[#9CA3AF]">overhead {overheadPct.toFixed(1)}%</p>
        </div>
      </div>

      {/* Stacked bar */}
      <div className="flex w-full h-3 rounded-full overflow-hidden bg-[#F1F5F9] mb-3" data-testid="fx-bar">
        {keys.map((k) => pcts[k] > 0 ? (
          <div key={k} className={FX_COLOR[k].bar} style={{ width: `${pcts[k]}%` }}
            title={`${FX_LABEL[k]} — ${pcts[k]}%`} />
        ) : null)}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        {keys.map((k) => (
          <div key={k} className={`border rounded p-2 ${FX_COLOR[k].bg} ${FX_COLOR[k].border}`}
            data-testid={`fx-${k}`}>
            <p className="text-[9px] uppercase font-bold tracking-wider opacity-80">{FX_LABEL[k]}</p>
            <p className={`font-heading text-base font-bold ${FX_COLOR[k].text}`}>
              ${Number(amounts[k] || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
            <p className="text-[10px] text-[#4B5563]">{(pcts[k] || 0).toFixed(1)}% of expenses</p>
          </div>
        ))}
      </div>
      {(pcts.unclassified || 0) > 0 && (
        <p className="mt-3 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2"
          data-testid="fx-unclassified-warning">
          {pcts.unclassified.toFixed(1)}% of expenses are unclassified. Open Chart of Accounts and
          assign each unclassified code a functional class so this report reflects reality.
        </p>
      )}
      <p className="mt-2 text-[10px] text-[#9CA3AF]">
        Total expenses: ${Number(totalExpenses || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </p>
    </div>
  );
};

const ACCENT = {
  green: 'bg-green-50 border-green-200 text-green-800',
  amber: 'bg-amber-50 border-amber-200 text-amber-800',
  brand: 'bg-[#FFF8F8] border-[#BA1826]/30 text-[#BA1826]',
};

const SummaryTile = ({ Icon, label, value, accent = 'brand', testid }) => (
  <div className={`border rounded-lg p-3 ${ACCENT[accent] || ACCENT.brand}`} data-testid={testid}>
    <div className="flex items-center justify-between mb-1">
      <span className="text-[9px] uppercase tracking-wider font-bold opacity-80">{label}</span>
      <Icon className="w-3.5 h-3.5 opacity-70" />
    </div>
    <p className="font-heading text-xl font-bold truncate" title={value}>{value}</p>
  </div>
);

const RollupTable = ({ title, rows, total, testid, emptyText }) => (
  <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden" data-testid={testid}>
    <div className="bg-[#FAFBFC] px-3 py-2 border-b border-[#E5E7EB] flex items-center justify-between">
      <p className="text-xs font-bold uppercase tracking-wider text-[#4B5563]">{title}</p>
      <span className="text-xs font-bold text-[#0A0A0A]">{`$${Number(total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</span>
    </div>
    {rows.length === 0 ? (
      <p className="px-3 py-4 text-xs text-[#9CA3AF] italic text-center">{emptyText}</p>
    ) : (
      <table className="w-full text-xs">
        <thead className="bg-[#F8F9FA] text-[#9CA3AF]">
          <tr>
            <th className="text-left px-3 py-1.5 w-16">Code</th>
            <th className="text-left px-3 py-1.5">Account</th>
            <th className="text-right px-3 py-1.5 w-14">#</th>
            <th className="text-right px-3 py-1.5 w-24">Amount</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.code} className="border-t border-[#E5E7EB]">
              <td className="px-3 py-1.5 font-mono font-bold">{r.code}</td>
              <td className="px-3 py-1.5">{r.name}</td>
              <td className="px-3 py-1.5 text-right text-[#9CA3AF]">{r.count}</td>
              <td className="px-3 py-1.5 text-right font-semibold">{`$${Number(r.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

export default TreasurerDashboardTab;
